import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import type {
  AiProviderKind, TenantAiSettings, UpdateTenantAiSettingsRequest, AiConnectionTestResult,
} from '../../types/models';

/**
 * Bring-your-own AI provider for an organisation.
 *
 * The same card serves two callers: the tenant admin on My Organisation
 * (their own tenant) and the platform admin on the Tenants page (set on a
 * tenant's behalf — how an enterprise customer is onboarded without a
 * screen-share). The caller supplies the API functions; the card owns the
 * form. A "Test connection" runs the UNSAVED form values through one tiny
 * request so a typo is caught before it is stored. The key is write-only:
 * the server only ever returns a mask.
 */
export interface TenantAiProviderApi {
  get: () => Promise<TenantAiSettings>;
  update: (data: UpdateTenantAiSettingsRequest) => Promise<TenantAiSettings>;
  remove: () => Promise<unknown>;
  /** Optional — the platform-admin surface has no test endpoint. */
  test?: (data: UpdateTenantAiSettingsRequest) => Promise<AiConnectionTestResult>;
}

const PROVIDER_LABEL: Record<AiProviderKind, string> = {
  Anthropic: 'Anthropic (direct)',
  Foundry: 'Microsoft Foundry',
  OpenAI: 'OpenAI',
};

// A real value in the box, not a placeholder that looks like one. The
// Foundry deployment name is the customer's choice but is almost always the
// model id; Alex typed nothing into a box that already appeared to say
// "claude-sonnet-5" and Save stayed disabled.
const DEFAULT_MODEL: Record<AiProviderKind, string> = {
  Anthropic: 'claude-sonnet-5',
  Foundry: 'claude-sonnet-5',
  OpenAI: 'gpt-4o',
};

interface FormState {
  provider: AiProviderKind;
  model: string;
  visionModel: string;
  apiKey: string;
  foundryResource: string;
}

function emptyForm(provider: AiProviderKind = 'Anthropic'): FormState {
  return { provider, model: DEFAULT_MODEL[provider], visionModel: '', apiKey: '', foundryResource: '' };
}

function formFrom(s: TenantAiSettings): FormState {
  return {
    provider: s.provider ?? 'Anthropic',
    model: s.model ?? DEFAULT_MODEL[s.provider ?? 'Anthropic'],
    visionModel: s.visionModel ?? '',
    apiKey: '',
    foundryResource: s.foundryResource ?? '',
  };
}

function toRequest(f: FormState): UpdateTenantAiSettingsRequest {
  const req: UpdateTenantAiSettingsRequest = {
    provider: f.provider,
    model: f.model.trim(),
  };
  if (f.apiKey.trim()) req.apiKey = f.apiKey.trim();
  if (f.provider === 'Foundry') req.foundryResource = f.foundryResource.trim();
  if (f.provider === 'OpenAI') req.visionModel = f.visionModel.trim();
  return req;
}

export default function TenantAiProviderCard({
  api, title = 'AI provider', compact = false,
}: { api: TenantAiProviderApi; title?: string; compact?: boolean }) {
  const [settings, setSettings] = useState<TenantAiSettings | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<AiConnectionTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The api object is often built inline by the caller (a new identity every
  // render). Hold it in a ref so a parent re-render never re-fetches and
  // wipes a half-typed key.
  const apiRef = useRef(api);
  apiRef.current = api;

  const load = useCallback(async () => {
    try {
      const s = await apiRef.current.get();
      setSettings(s);
      setForm(s.provider ? formFrom(s) : emptyForm());
      setEditing(!s.provider);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load AI provider settings.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const changeProvider = (provider: AiProviderKind) => {
    setTestResult(null);
    setForm(prev => ({
      ...prev,
      provider,
      // Keep a model the admin typed; swap only when it was the other provider's default.
      model: Object.values(DEFAULT_MODEL).includes(prev.model) || prev.model === '' ? DEFAULT_MODEL[provider] : prev.model,
    }));
  };

  const hasStoredKey = !!settings?.apiKeyMasked;
  const keyReady = form.apiKey.trim().length > 0 || hasStoredKey;
  // Why Save is disabled, in words. A greyed-out button with no reason is
  // indistinguishable from a broken one — and a password manager that
  // autofills the key without firing an input event leaves the form
  // thinking the key is empty while the field shows dots.
  const missing: string[] = [];
  if (!settings?.planAllows) missing.push('your plan does not include a custom AI provider');
  if (!keyReady) missing.push('enter the API key (type or paste it — autofill may not register)');
  if (form.model.trim().length === 0) missing.push(form.provider === 'Foundry' ? 'enter the deployment name' : 'enter the model');
  if (form.provider === 'Foundry' && form.foundryResource.trim().length === 0) missing.push('enter the Foundry resource name');
  const ready = missing.length === 0;

  const onTest = async () => {
    if (!apiRef.current.test) return;
    setTesting(true); setTestResult(null); setError(null);
    try {
      setTestResult(await apiRef.current.test!(toRequest(form)));
    } catch (e: any) {
      setTestResult({ succeeded: false, message: e?.response?.data?.error ?? 'Test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    setSaving(true); setError(null); setNotice(null);
    try {
      const saved = await apiRef.current.update(toRequest(form));
      setSettings(saved);
      setForm(formFrom(saved));
      setEditing(false);
      setTestResult(null);
      setNotice(saved.inUse
        ? `Cadence now uses your ${PROVIDER_LABEL[saved.provider ?? 'Anthropic']} key for this organisation.`
        : 'Saved. Fill in the remaining fields to activate.');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const onToggleActive = async (isActive: boolean) => {
    setError(null);
    try {
      const saved = await apiRef.current.update({ isActive });
      setSettings(saved);
      setNotice(isActive ? 'Your provider is active again.' : 'Paused — Cadence is using the Stratium-managed provider.');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not update.');
    }
  };

  const onRemove = async () => {
    if (!window.confirm('Remove your AI provider? Cadence will go back to the Stratium-managed provider and the platform usage limit will apply again.')) return;
    setError(null);
    try {
      await apiRef.current.remove();
      await load();
      setNotice('Removed. Cadence is using the Stratium-managed provider.');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not remove.');
    }
  };

  if (!settings) {
    return (
      <Card className={compact ? '' : 'mt-3'}>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {error ?? 'Loading AI provider…'}
        </CardContent>
      </Card>
    );
  }

  const statusBadge = settings.inUse
    ? <Badge className="bg-emerald-600 text-white">Your key in use</Badge>
    : settings.provider && !settings.isActive
      ? <Badge variant="secondary">Paused</Badge>
      : settings.provider && !settings.isConfigured
        ? <Badge variant="secondary">Incomplete</Badge>
        : <Badge variant="outline">Stratium-managed</Badge>;

  return (
    <Card className={compact ? '' : 'mt-3'} data-testid="tenant-ai-provider">
      <CardContent className="pt-6">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">{title}</h3>
          </div>
          {statusBadge}
        </div>
        <p className="mb-3 text-sm text-muted-foreground">
          Use your own Anthropic, Microsoft Foundry or OpenAI key for every AI feature — Plan day, meeting briefs,
          Quick Capture, voice notes, image and transcript analysis. Your prompts then go to your provider under your
          agreement with them, and the Stratium AI usage limit no longer applies. The key is stored encrypted and is
          never shown again.
        </p>

        {!settings.planAllows && (
          <Alert variant="warning" className="mb-3">
            <AlertDescription>Bring-your-own AI provider is available on Professional and Enterprise plans.</AlertDescription>
          </Alert>
        )}
        {error && <Alert variant="error" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
        {notice && <Alert variant="success" className="mb-3"><AlertDescription>{notice}</AlertDescription></Alert>}

        {!editing && settings.provider ? (
          <div className="flex flex-col gap-3">
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <dt className="text-muted-foreground">Provider</dt><dd>{PROVIDER_LABEL[settings.provider]}</dd>
              <dt className="text-muted-foreground">{settings.provider === 'Foundry' ? 'Deployment' : 'Model'}</dt><dd className="font-mono">{settings.model}</dd>
              {settings.provider === 'Foundry' && (
                <><dt className="text-muted-foreground">Resource</dt><dd className="font-mono">{settings.foundryResource}.services.ai.azure.com</dd></>
              )}
              {settings.provider === 'OpenAI' && settings.visionModel && (
                <><dt className="text-muted-foreground">Vision model</dt><dd className="font-mono">{settings.visionModel}</dd></>
              )}
              <dt className="text-muted-foreground">API key</dt><dd className="font-mono">{settings.apiKeyMasked ?? '—'}</dd>
              {settings.lastTestedAt && (
                <>
                  <dt className="text-muted-foreground">Last test</dt>
                  <dd className="flex items-center gap-1">
                    {settings.lastTestSucceeded
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : <XCircle className="h-4 w-4 text-destructive" />}
                    <span>{new Date(settings.lastTestedAt).toLocaleString()}</span>
                  </dd>
                </>
              )}
            </dl>
            <div className="flex flex-wrap items-center gap-2">
              <div className="mr-auto flex items-center gap-2">
                <Switch checked={settings.isActive} onCheckedChange={onToggleActive} disabled={!settings.isConfigured} />
                <span className="text-sm">{settings.isActive ? 'Active' : 'Paused'}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setEditing(true); setNotice(null); }}>Edit</Button>
              <Button variant="ghost" size="sm" onClick={onRemove}>Remove</Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Provider</Label>
                <Select value={form.provider} onValueChange={(v) => changeProvider(v as AiProviderKind)} disabled={!settings.planAllows}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PROVIDER_LABEL) as AiProviderKind[]).map(p => (
                      <SelectItem key={p} value={p}>{PROVIDER_LABEL[p]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.provider === 'Foundry' && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ai-foundry-resource">Foundry resource name</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      id="ai-foundry-resource"
                      value={form.foundryResource}
                      onChange={e => set('foundryResource', e.target.value)}
                      placeholder="e.g. my-resource"
                      autoComplete="off"
                      disabled={!settings.planAllows}
                    />
                    <span className="whitespace-nowrap text-xs text-muted-foreground">.services.ai.azure.com</span>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ai-model">{form.provider === 'Foundry' ? 'Deployment name' : 'Model'}</Label>
                <Input
                  id="ai-model"
                  value={form.model}
                  onChange={e => set('model', e.target.value)}
                  placeholder={`e.g. ${DEFAULT_MODEL[form.provider]}`}
                  autoComplete="off"
                  disabled={!settings.planAllows}
                />
              </div>

              {form.provider === 'OpenAI' && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="ai-vision-model">Vision model (image analysis)</Label>
                  <Input
                    id="ai-vision-model"
                    value={form.visionModel}
                    onChange={e => set('visionModel', e.target.value)}
                    placeholder="e.g. gpt-4o"
                    autoComplete="off"
                    disabled={!settings.planAllows}
                  />
                </div>
              )}

              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label htmlFor="ai-api-key">API key</Label>
                <Input
                  id="ai-api-key"
                  type="password"
                  value={form.apiKey}
                  onChange={e => set('apiKey', e.target.value)}
                  placeholder={hasStoredKey ? `Leave blank to keep ${settings.apiKeyMasked}` : (form.provider === 'OpenAI' ? 'sk-…' : 'sk-ant-…')}
                  autoComplete="new-password"
                  disabled={!settings.planAllows}
                />
              </div>
            </div>

            {testResult && (
              <Alert variant={testResult.succeeded ? 'success' : 'error'}>
                <AlertDescription className="flex items-start gap-2">
                  {testResult.succeeded
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                    : <XCircle className="mt-0.5 h-4 w-4 shrink-0" />}
                  <span>{testResult.message}</span>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {api.test && (
                <Button variant="outline" size="sm" onClick={onTest} disabled={!ready || testing} title={ready ? undefined : missing.join('; ')}>
                  {testing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  Test connection
                </Button>
              )}
              <Button size="sm" onClick={onSave} disabled={!ready || saving} title={ready ? undefined : missing.join('; ')}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {!ready && (
                <span className="text-xs text-muted-foreground" data-testid="ai-provider-missing">
                  To save: {missing.join('; ')}.
                </span>
              )}
              {settings.provider && (
                <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setForm(formFrom(settings)); setTestResult(null); }}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
